import { isInDisplayWindow, getLastScheduleTime, getDate, formatTime, parseTimeToDate } from "./dateUtils";
import { rambam_1_chapter, rambam_3_chapter } from '../calendar/rambam.js';
import { getWeeklyPortionForDate } from '../calendar/calendar.js';
import { getPirkeiAvotPerek } from '../calendar/pirkeiAvot.js';
import { getTanyaCycle } from '../calendar/tanya.js';
import { HDate, Sedra, HebrewDateEvent, gematriya, FastDayEvent, Zmanim } from '@hebcal/core';
import { ZmanEnum, weeklyZmanim, WeeklyZmanimEnum } from '../calendar/zmanim.js';

/**
 * Strips Hebrew nikkud (vowel marks) and cantillation marks from Hebrew text
 * @param {string} text - Hebrew text with nikkud
 * @returns {string} Hebrew text without nikkud
 */
export function stripNikkud(text) {
    if (!text) return text;
    // Remove Hebrew vowel marks (nikkud) and cantillation marks
    // Exclude Hebrew maqaf (U+05BE) which is the dash used to connect Hebrew words
    return text.replace(/[\u0591-\u05BD\u05BF-\u05C7]/g, '');
}

/**
 * Determines if a parsha (or any parsha in combined parshas) marks the end of a Torah book (chazak)
 * @param {string} parsha - The parsha string (may contain multiple parshas separated by delimiters)
 * @returns {boolean} True if any parsha in the string is a book-ending parsha
 */
export function isChazak(parsha) {
    if (!parsha) return false;
    
    // Book-ending parshas (without nikkud)
    const bookEndingParshas = [
        'ויחי',      // Genesis - Vayechi
        'פקודי',     // Exodus - Pekudei
        'בחקתי',     // Leviticus - Bechukotai
        'מסעי',      // Numbers - Masei
        'וזאת הברכה' // Deuteronomy - V'Zot HaBerachah
    ];
    
    // Strip nikkud from the input parsha
    const cleanParsha = stripNikkud(parsha);
    
    // Remove common prefixes like "פרשת" or "שבת,"
    let parshaText = cleanParsha.replace(/^פרשת\s+/, '').replace(/^שבת,\s*/, '').trim();
    
    // First check if the entire string (after removing prefixes) matches a book-ending parsha
    // This handles cases like "וזאת הברכה" which contains a space but is a single parsha
    if (bookEndingParshas.some(endingParsha => parshaText === endingParsha)) {
        return true;
    }
    
    // Split on delimiters that indicate combined parshas
    // Include Hebrew maqaf (־) U+05BE, regular hyphen (-), and en dash (–)
    let parshaNames = parshaText.split(/[-–\u05BE]+/).filter(name => name.trim().length > 0);
    
    // If splitting on explicit delimiters didn't produce multiple parts, try splitting on spaces
    // This handles cases like "ויקהל פקודי" where a space separates two parshas
    // Note: We do this after checking the full string, so "וזאת הברכה" is already handled
    if (parshaNames.length === 1 && parshaText.includes(' ')) {
        parshaNames = parshaText.split(/\s+/).filter(name => name.trim().length > 0);
    }
    
    // Check if any individual parsha name exactly matches a book-ending parsha
    // Use exact matching to avoid false positives (e.g., "חקת" matching "בחקתי")
    return parshaNames.some(name => {
        const trimmedName = name.trim();
        return bookEndingParshas.some(endingParsha => 
            trimmedName === endingParsha
        );
    });
}

export function loadRambamData(date) {
    const oneChapter = rambam_1_chapter(date);
    const threeChapters = rambam_3_chapter(date);

    // ====== Build the "3 perakim" content with partial grouping ======

    // We'll group consecutive perakim with the same sefer
    const groups = [];
    if (threeChapters.length > 0) {
        // Start off our "current group" with the very first perek
        let currentSefer = threeChapters[0].heb.sefer;
        let startPerek = threeChapters[0].heb.perek;
        let endPerek = startPerek;

        // Go through chapters, grouping them where the sefer is the same
        for (let i = 1; i < threeChapters.length; i++) {
            const { sefer, perek } = threeChapters[i].heb;
            if (sefer === currentSefer) {
                // Same sefer => expand the group range
                endPerek = perek;
            } else {
                // Different sefer => finalize current group and start a new one
                groups.push({
                    sefer: currentSefer,
                    start: startPerek,
                    end: endPerek,
                });

                // Begin a new group
                currentSefer = sefer;
                startPerek = perek;
                endPerek = perek;
            }
        }

        // Push the final group
        groups.push({
            sefer: currentSefer,
            start: startPerek,
            end: endPerek,
        });
    }

    // Convert groups to the desired string
    const threePerokimContent = groups
        .map(({ sefer, start, end }) => {
            if (start === end) {
                return `${sefer} ${start}`;
            } else {
                return `${sefer} ${start}-${end}`;
            }
        })
        .join(' & ');

    // ====== Build the "פרק אחד" content ======
    // rambam_1_chapter returns an array: usually one unit, but two on the day
    // chabad bundles the Confession + Grace nusach onto a single study day.
    const oneChapterContent = oneChapter
        .map((u) => `${u.heb.sefer} - ${u.heb.perek}`)
        .join(' & ');

    // ====== Prepare final return ======
    return [
        {
            title: 'פרק אחד',
            content: oneChapterContent,
        },
        {
            title: 'שלושה פרקים',
            content: threePerokimContent,
        },
    ];
}


export async function loadTanyaData(date, dailyStudies) {
    // The Tanya year-type (leap/regular) follows the cycle's 19-Kislev anchor,
    // not the current civil Hebrew year — see getTanyaCycle for why.
    const { yearType, monthName, day } = getTanyaCycle(date);

    try {
        const response = await fetch(`/tanya/${yearType}/${monthName}.json?t=${Date.now()}`);
        if (!response.ok) {
            throw new Error(`Failed to fetch Tanya for month ${monthName}`);
        }
        const tanyaData = await response.json();
        const todayTanya = tanyaData[day];

        if (todayTanya) {
            const firstThreeWords = todayTanya.beginWords.split(' ').slice(0, 3).join(' ').replace(':', '').trim();
            const lastThreeWords = todayTanya.endWords.split(' ').slice(-3).join(' ');
            dailyStudies.push({
                title: 'תניא',
                content: `${todayTanya.sefer} ${todayTanya.perek} (${firstThreeWords} - ${lastThreeWords})`
            });
        }

        return dailyStudies;
    } catch (error) {
        console.error('Error fetching Tanya data:', error);
        return dailyStudies;
    }
}

export function loadCoreData(date) {
    const hDate = new HDate(date);
    const weeklyPortion = new Sedra(hDate.getFullYear()).getString(hDate, 'he');
    const hebrewDate = new HebrewDateEvent(hDate).renderBrief('he').replace('׳', '');
    const hebrewDay = gematriya(hDate.dd);

    return { hebrewDate, weeklyPortion, hebrewDay };
}

export function getPrintSchedules(now, data, zmanim) {
    const dayOfWeek = now.getDay();
    const hour = now.getHours();
    const minute = now.getMinutes();
    const currentTime = now.getHours() * 60 + now.getMinutes();
    const tzeisTime =
        zmanim.getZman(ZmanEnum.TZEIS)?.getTime() || null;

    let shabbosDate = new Date(now);
    
    let switchToNextWeek = false;
    if (dayOfWeek > 4 || (dayOfWeek === 4 && (hour === 21 && minute >= 30))) {
        switchToNextWeek = true;
        shabbosDate.setDate(shabbosDate.getDate() + ((6 - dayOfWeek + 7) % 7));
    }else{
        shabbosDate.setDate(shabbosDate.getDate() - ((dayOfWeek - 6 + 7) % 7));
    }


    let parsha = getWeeklyPortionForDate(shabbosDate);
    let weeklyZmanimArray = weeklyZmanim(shabbosDate);
    data.weeklySchedules = resolveWeeklyZmanim(data.weeklySchedules, weeklyZmanimArray, shabbosDate);

    let shabbosDay = data.weeklySchedules.shabbosDay;

    if (weeklyZmanimArray.isShabbosMevorchim()){
        console.log('print is shabbos mevorchim');
        shabbosDay = data.weeklySchedules.shabbosDay_mevorchim;
    }else{
        console.log('print is NOT shabbos mevorchim')
    }

    return {
        fullWeeklySchedules: {
            fridayNight: data.weeklySchedules.fridayNight,
            shabbosDay: shabbosDay,
            shabbosDay_mevorchim: data.weeklySchedules.shabbosDay_mevorchim,
            shabbosAfternoon: data.weeklySchedules.shabbosAfternoon,
            sunday: data.weeklySchedules.sunday,
            weekdays: data.weeklySchedules.weekdays,
            fridayMorning: data.weeklySchedules.fridayMorning
        },
        dateRange: weeklyZmanimArray.dateRange,
        parsha: parsha,
        mevorchim: weeklyZmanimArray.isShabbosMevorchim(),
        chazak: isChazak(parsha)
    };
}

// A helper that, given the day-of-week, time of day, etc.,
// returns { current, upcoming } from the normal weeklySchedules.
function getWeeklySchedulesForNow(now, data, zmanim) {
    // Validate input data
    if (!data || !data.weeklySchedules) {
        console.error('getWeeklySchedulesForNow: missing weeklySchedules', data);
        return { current: null, upcoming: null, weeklyZmanim: null, parsha: null };
    }
    
    const dayOfWeek = now.getDay();
    const hour = now.getHours();
    const minute = now.getMinutes();
    const currentTime = now.getHours() * 60 + now.getMinutes();
    const tzeisTime =
        zmanim.getZman(ZmanEnum.TZEIS)?.getTime() || null;

    // Default to weekdays schedule, but validate it exists
    let currentSchedule = data.weeklySchedules.weekdays || null;
    let upcomingSchedule = null;
    
    // If weekdays doesn't exist, log error and return null
    if (!currentSchedule) {
        console.error('getWeeklySchedulesForNow: missing weekdays schedule', data.weeklySchedules);
        return { current: null, upcoming: null, weeklyZmanim: null, parsha: null };
    }

    let shabbosDate = new Date(now);
    shabbosDate.setDate(shabbosDate.getDate() - ((dayOfWeek - 6 + 7) % 7));

    let nextShabbosDate = new Date(now);
    nextShabbosDate.setDate(nextShabbosDate.getDate() + ((6 - dayOfWeek + 7) % 7));

    let switchToNextWeek = false;
    if ((dayOfWeek === 4 && (hour === 21 && minute >= 30)) || dayOfWeek > 4) {
        switchToNextWeek = true;
        shabbosDate = nextShabbosDate;
    }

    let parsha = getWeeklyPortionForDate(nextShabbosDate);
    let weeklyZmanimArray = weeklyZmanim(shabbosDate);
    data.weeklySchedules = resolveWeeklyZmanim(data.weeklySchedules, weeklyZmanimArray, shabbosDate);

    let shabbosDay = data.weeklySchedules.shabbosDay;
    if (weeklyZmanimArray.isShabbosMevorchim())
        shabbosDay = data.weeklySchedules.shabbosDay_mevorchim;

    if (switchToNextWeek) {
        currentSchedule = data.weeklySchedules.weekdays;
        upcomingSchedule = upcomingSchedule = data.weeklySchedules.fridayNight;
    }

    switch (dayOfWeek) {
        case 4: // Thursday after 10 PM
            if (switchToNextWeek) {
                currentSchedule = data.weeklySchedules.fridayMorning;
                upcomingSchedule = data.weeklySchedules.fridayNight;
            }
            break;

        case 5: { // Friday morning logic

            if (currentTime < 7.5 * 60) {
                currentSchedule = data.weeklySchedules.fridayMorning;
                upcomingSchedule = data.weeklySchedules.fridayNight;
            }

            if (currentTime >= 7.5 * 60) {
                currentSchedule = data.weeklySchedules.fridayNight;
                upcomingSchedule = shabbosDay;
            }
            const fridayNightEnd = getLastScheduleTime(data.weeklySchedules.fridayNight);
            if (fridayNightEnd && now.getTime() >= fridayNightEnd + 30 * 60 * 1000) {
                currentSchedule = shabbosDay;
                upcomingSchedule = data.weeklySchedules.shabbosAfternoon;
            }
            break;
        }

        case 6: { // Shabbos logic
            if (currentTime < 13 * 60) {
                currentSchedule = shabbosDay;
                upcomingSchedule = data.weeklySchedules.shabbosAfternoon;
            } else {
                currentSchedule = data.weeklySchedules.shabbosAfternoon;
                upcomingSchedule = data.weeklySchedules.sunday;
            }
            break;
        }

        case 0: { // Sunday logic
            if (tzeisTime && now.getTime() < tzeisTime) {
                currentSchedule = data.weeklySchedules.sunday;
                upcomingSchedule = data.weeklySchedules.weekdays;
            } else {
                const sundayEnd = getLastScheduleTime(data.weeklySchedules.sunday);
                if (sundayEnd && now.getTime() >= sundayEnd + 30 * 60 * 1000) {
                    currentSchedule = data.weeklySchedules.weekdays;
                    upcomingSchedule = null;
                } else {
                    currentSchedule = data.weeklySchedules.sunday;
                    upcomingSchedule = data.weeklySchedules.weekdays;
                }
            }
            break;
        }

        default: // Monday-Thursday (dayOfWeek = 1..3)
            currentSchedule = data.weeklySchedules.weekdays;
            upcomingSchedule = null;
            break;
    }

    return { current: currentSchedule, upcoming: upcomingSchedule, weeklyZmanim: weeklyZmanimArray, parsha: parsha };
}


export function getVisibleSchedules(data, zmanim) {
    // Validate input
    if (!data || !data.weeklySchedules) {
        console.error('getVisibleSchedules: missing weeklySchedules', data);
        return {
            visibleSchedule: null,
            upcomingSchedule: null,
            parsha: null,
            mevorchim: false,
            chazak: false
        };
    }
    
    const dateTime = getDate();

    const currentWeeklySchedule = getWeeklySchedulesForNow(dateTime, data, zmanim);
    let currentSchedule = currentWeeklySchedule.current;
    let upcomingSchedule = currentWeeklySchedule.upcoming;
    
    // If currentSchedule is null, return early with null values
    if (!currentSchedule) {
        console.error('getVisibleSchedules: currentSchedule is null', {
            currentWeeklySchedule,
            hasData: !!data,
            hasWeeklySchedules: !!data.weeklySchedules
        });
        return {
            visibleSchedule: null,
            upcomingSchedule: null,
            parsha: currentWeeklySchedule.parsha || null,
            mevorchim: false,
            chazak: false
        };
    }
   // console.log('FILTER _ START datedSchedules', data.datedSchedules);

    const datedSchedules = Object.values(data.datedSchedules || {})
        .filter(section =>
            isInDisplayWindow(section.displayDateTime, section.expirationDateTime)
        )
        .sort((a, b) => a.sortOrder - b.sortOrder);

    datedSchedules.forEach(schedule => {
        schedule = resolveScheduleTimes(schedule, zmanim);
    });

   // console.log('FILTER END _ datedSchedules', datedSchedules);

    if (datedSchedules.length >= 2) {
        // If 2 or more dated schedules are active, use the first two:
        currentSchedule = datedSchedules[0];
        upcomingSchedule = datedSchedules[1];
    } else if (datedSchedules.length === 1) {
        currentSchedule = datedSchedules[0];
        upcomingSchedule = currentWeeklySchedule.current;

    } else {
        // No dated schedules => do nothing special,
        // keep the currentWeeklySchedule day-of-week picks
    }

    // Resolve times for what we're about to show
    //resolveScheduleTimes(currentSchedule, zmanim);
    //resolveScheduleTimes(upcomingSchedule, zmanim);
    //console.log('currentSchedule', data.weeklySchedules.sunday, currentWeeklySchedule.weeklyZmanim[WeeklyZmanimEnum.SUNDAY] );
    
    return {
        visibleSchedule: currentSchedule,
        upcomingSchedule: upcomingSchedule,
        parsha: currentWeeklySchedule.parsha,
        mevorchim: currentWeeklySchedule.weeklyZmanim.isShabbosMevorchim(),
        chazak: isChazak(currentWeeklySchedule.parsha)
    };
}

function resolveWeeklyZmanim(schedules, weeklyZmanim, targetDate = null) {
    // If targetDate is not provided, use current date as before
    const dateForPlaceholders = targetDate || getDate();

    //console.log('resolveWeeklyZmanim', schedules, weeklyZmanim);
    //console.log('BEFORE', schedules['fridayNight']);
    for (const [zmanType, schedule] of Object.entries(schedules)) {
        switch (zmanType) {
            case 'fridayNight':
                schedules['fridayNight'] = resolveScheduleTimes(schedule, weeklyZmanim[WeeklyZmanimEnum.FRIDAY_NIGHT], dateForPlaceholders, true);
                break;
            case 'shabbosDay':
                schedules['shabbosDay'] = resolveScheduleTimes(schedule, weeklyZmanim[WeeklyZmanimEnum.SHABBOS_DAY], dateForPlaceholders, true);
                break;
            case 'shabbosDay_mevorchim':
                schedules['shabbosDay_mevorchim'] = resolveScheduleTimes(schedule, weeklyZmanim[WeeklyZmanimEnum.SHABBOS_DAY], dateForPlaceholders, true);
                break;
            case 'shabbosAfternoon':
                schedules['shabbosAfternoon'] = resolveScheduleTimes(schedule, weeklyZmanim[WeeklyZmanimEnum.SHABBOS_DAY], dateForPlaceholders, true);
                break;
            case 'fridayMorning':
                schedules['fridayMorning'] = resolveScheduleTimes(schedule, weeklyZmanim[WeeklyZmanimEnum.WEEKDAYS], dateForPlaceholders, true);
                break;
            case 'sunday':
            case 'weekdays':
                schedules[zmanType] = resolveScheduleTimes(schedule, weeklyZmanim[WeeklyZmanimEnum.WEEKDAYS], dateForPlaceholders, true);
                break;
        }
    }
    //console.log('AFTER', schedules);

    return schedules;

}

/**
 * Detects placeholders in a string in the format {{PLACEHOLDER_NAME}}
 * @param {string} text - The text to check for placeholders
 * @returns {Array|null} - Array of placeholder names found or null if none found
 */
export function detectPlaceholders(text) {
    if (!text) return null;
    
    const placeholderRegex = /\{\{([A-Z_]+)\}\}/g;
    const placeholders = [];
    let match;
    
    while ((match = placeholderRegex.exec(text)) !== null) {
        placeholders.push(match[1]);
    }
    
    return placeholders.length > 0 ? placeholders : null;
}

/**
 * Gets replacement value for a placeholder
 * @param {string} placeholder - The placeholder name
 * @param {Date} date - The date to use for context-dependent placeholders
 * @returns {string|null} - The replacement value or null if no replacement found
 */
export function getPlaceholderReplacement(placeholder, date) {
    switch (placeholder) {
        case 'PIRKEI_AVOS_PEREK':
            return getPirkeiAvotPerek(date) || '';
        // Add more placeholder handlers here as needed
        default:
            return null;
    }
}

/**
 * Replaces all placeholders in a text with their corresponding values
 * @param {string} text - The text containing placeholders
 * @param {Date} date - The date to use for context-dependent placeholders
 * @returns {string} - The text with placeholders replaced
 */
export function replacePlaceholders(text, date) {
    if (!text) return text;
    
    const placeholders = detectPlaceholders(text);
    if (!placeholders) return text;
    
    let result = text;
    
    placeholders.forEach(placeholder => {
        const replacement = getPlaceholderReplacement(placeholder, date);
        if (replacement !== null) {
            result = result.replace(`{{${placeholder}}}`, replacement);
        }
    });
    
    return result;
}

/**
 * Gets a preview of what a zman or relative zman would resolve to
 * @param {Object} item - The schedule item with zman or relativeZman
 * @param {Object} zmanimObj - The zmanim object to use for resolution
 * @returns {string} - The resolved time as a string, or a message if resolution fails
 */
export function getZmanPreview(item, zmanimObj, targetDate = null) {
  try {
    if (!item || !zmanimObj) return "";
    
    // Check if the item is using the new relativeDirection format with rounding
    if (item.relativeZman && item.relativeDirection && !item.relativeDirection.includes('_')) {
      // Convert old format to new format with exact rounding
      item = { 
        ...item, 
        relativeDirection: `${item.relativeDirection}_exact` 
      };
    }
    
    // Create a temporary copy of the item to avoid modifying the original
    const tempItem = { ...item };
    
    // Create a temporary "schedule" structure that the resolveScheduleTimes function expects
    const tempSchedule = {
      schedule: [tempItem]
    };
    
    // Use the existing resolveScheduleTimes function to resolve the item
    // Pass the targetDate if provided
    const resolvedSchedule = resolveScheduleTimes(tempSchedule, zmanimObj, targetDate);
    
    // Extract the resolved value from the first (and only) item in the resolved schedule
    if (resolvedSchedule && 
        resolvedSchedule.schedule && 
        resolvedSchedule.schedule.length > 0 && 
        resolvedSchedule.schedule[0].value) {
      return resolvedSchedule.schedule[0].value;
    }
    
    return "";
  } catch (err) {
    console.error("Error generating zman preview:", err);
    return "";
  }
}

export function resolveScheduleTimes(schedule, zmanim, targetDateForPlaceholders = null, isWeeklySchedule = false) {
    // Use provided target date or fall back to current date
    const dateTime = targetDateForPlaceholders || getDate();
    
    if (schedule && schedule.schedule) {       
        schedule.schedule = schedule.schedule.map(item => {
            try {
                // For weekly schedules, we don't need date information
                // so no need to preserve it
                
                if (!item.value && item.zman) {
                    if (item.zman == 'MOLAD' || item.zman == 'SEVENTH_HOUR') {
                        const zman = zmanim.zmanim.find(z => z.zman === item.zman);
                        if (zman && zman.value) {
                            item.value = zman.value;
                            delete item.zman;
                        } else {
                            item.hideItem = true;
                        }
                    } else if (item.zman == 'FAST_END' || item.zman == 'FAST_START' || item.zman == 'EARLIEST_SHKIAH_SUN_THUR' || item.zman == 'LATEST_SHKIAH_SUN_THUR' || item.zman == 'TZEIS8_5') {
                        const zman = zmanim.zmanim.find(z => z.zman === item.zman);
                        if (zman && zman.time) {
                            item.value = formatTime(zman.time);
                            delete item.zman;
                        } else {
                            item.hideItem = true;
                        }
                    } 
                    else {
                        const time = zmanim.getZman(item.zman);
                        if (time) {
                            item.value = formatTime(time);
                            delete item.zman;
                        } else {
                            item.value = item.zman;
                        }
                    }
                } else if (item.relativeZman) {
                    // Handle relative zmanim
                    
                    const baseZman = zmanim.getZman(item.relativeZman);
                    
                    if (baseZman) {
                        // Create a new date object to avoid modifying the original
                        const adjustedTime = new Date(baseZman.getTime());
                        
                        // Parse the direction and rounding (support both old and new formats)
                        let direction = item.relativeDirection;
                        let rounding = 'exact';
                        
                        // Check if we're using the new format with rounding
                        if (item.relativeDirection && item.relativeDirection.includes('_')) {
                            const parts = item.relativeDirection.split('_');
                            direction = parts[0];
                            rounding = parts[1];
                        }
                        
                        // Add or subtract minutes based on the direction
                        const minutesInMs = item.relativeMinutes * 60 * 1000;
                        if (direction === 'before') {
                            adjustedTime.setTime(adjustedTime.getTime() - minutesInMs);
                        } else {
                            adjustedTime.setTime(adjustedTime.getTime() + minutesInMs);
                        }
                        
                        // Apply rounding if specified
                        if (rounding === 'roundDown') {
                            // Round down to the nearest 5 minutes
                            const minutes = adjustedTime.getMinutes();
                            const roundedMinutes = Math.floor(minutes / 5) * 5;
                            adjustedTime.setMinutes(roundedMinutes);
                            adjustedTime.setSeconds(0);
                            adjustedTime.setMilliseconds(0);
                        } else if (rounding === 'roundUp') {
                            // Round up to the nearest 5 minutes
                            const minutes = adjustedTime.getMinutes();
                            const remainder = minutes % 5;
                            if (remainder === 0 && (adjustedTime.getSeconds() === 0 && adjustedTime.getMilliseconds() === 0)) {
                                // Already at a 5-minute mark with no seconds/milliseconds, don't change
                            } else {
                                const roundedMinutes = Math.ceil(minutes / 5) * 5;
                                adjustedTime.setMinutes(roundedMinutes);
                                adjustedTime.setSeconds(0);
                                adjustedTime.setMilliseconds(0);
                            }
                        }
                        // 'exact' - no rounding needed
                        
                        item.value = formatTime(adjustedTime);
                        
                        // Clean up the relative zman properties
                        delete item.relativeZman;
                        delete item.relativeMinutes;
                        delete item.relativeDirection;
                    } else {
                        // Handle case where the zman doesn't exist
                        console.warn('resolveScheduleTimes - baseZman not found:', {
                            itemTitle: item.title,
                            relativeZman: item.relativeZman,
                            availableZmanim: zmanim.zmanim.map(z => z.zman)
                        });
                        
                        // Parse direction for display
                        let directionDisplay = item.relativeDirection;
                        if (item.relativeDirection && item.relativeDirection.includes('_')) {
                            const parts = item.relativeDirection.split('_');
                            directionDisplay = `${parts[0]} (${parts[1]})`;
                        }
                        
                        item.value = `${item.relativeMinutes} min ${directionDisplay} ${item.relativeZman}`;
                        delete item.relativeZman;
                        delete item.relativeMinutes;
                        delete item.relativeDirection;
                    }
                } else if (item.time) {
                    let itemTime = parseTimeToDate(item.time);
                    item.value = formatTime(itemTime);
                    delete item.time;
                }
                
                // Process placeholders in the value field if it exists
                if (item.value && typeof item.value === 'string') {
                    item.value = replacePlaceholders(item.value, dateTime);
                }
                
                // For weekly schedules, we don't preserve date information
                // But for dated schedules, we need to keep the date to support proper highlighting
                if (item.date && isWeeklySchedule) {
                    delete item.date;
                }
                // For dated schedules, preserve the date property
                
                return item;
            } catch (err) {
                console.log(err, item);
                //debugger;
            }
        });
    }
    return schedule;
}
